import { Components, Theme } from '@mui/material';
import { designTokens } from '../themes/design-tokens';

export const tableTheme: Components<Theme> = {
  MuiTableCell: {
    styleOverrides: {
      head: ({ theme }) => ({
        // Title label styling
        fontFamily: designTokens.typography.fontFamily.primary,
        fontSize: '0.75rem', // 12px using rem (12px / 16px = 0.75rem)
        fontWeight: designTokens.typography.fontWeights.regular, // 400
        lineHeight: 1.4, // 140%
        letterSpacing: '0.005em', // 0.5%
        color: theme.palette.text?.second || theme.palette.text.secondary,
        fontStyle: 'normal',
      }),
      body: ({ theme }) => ({
        // Row label styling - same as head but with text.main color
        fontFamily: designTokens.typography.fontFamily.primary,
        fontSize: '0.75rem', // 12px using rem
        fontWeight: designTokens.typography.fontWeights.regular, // 400
        lineHeight: 1.4, // 140%
        letterSpacing: '0.005em', // 0.5%
        color: theme.palette.text?.main || theme.palette.text.primary,
        fontStyle: 'normal',

        // Add border between rows
        borderBottom: `1px solid ${theme.palette.text?.fifth || theme.palette.divider}`,
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Default row height: 36px = 2.25rem
        height: '2.25rem',

        '&.MuiTableRow-hover:hover': {
          backgroundColor: `${theme.palette.background?.button || theme.palette.action.hover}20`, // 20% opacity (20 in hex = ~12.5%, using 20 for closer to 20%)
        },

        // Fullscreen (expanded) row height: 52px = 3.25rem
        '&[data-fullscreen="true"]': {
          height: '3.25rem',

          // Ensure all child cells maintain same height in fullscreen
          '& .MuiTableCell-root': {
            height: '3.25rem',
          },
        },

        // Selected or expanded row styling
        '&.Mui-selected, &[data-expanded="true"]': {
          backgroundColor: `${theme.palette.background?.button || 'rgba(0, 0, 0, 0.08)'} !important`, // Using fallback color
          color: theme.palette.text.fifth,

          '&:hover': {
            backgroundColor: `${theme.palette.background?.button || 'rgba(0, 0, 0, 0.12)'} !important`, // Darker on hover
          },
        },
      }),
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Ensure sort labels inherit the same styling as headers
        fontFamily: designTokens.typography.fontFamily.primary,
        fontSize: '0.75rem',
        fontWeight: designTokens.typography.fontWeights.regular,
        lineHeight: 1.4,
        letterSpacing: '0.005em',
        color: theme.palette.text?.second || theme.palette.text.secondary,

        '&.Mui-active': {
          color: theme.palette.text?.second || theme.palette.text.secondary,
        },

        '&:hover': {
          color: theme.palette.text?.main || theme.palette.text.primary,
        },
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Main pagination container styling
        '& .MuiTablePagination-toolbar': {
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },

        // Page navigation buttons (< >)
        '& .MuiTablePagination-actions button': {
          width: '32px',
          height: '32px',
          borderRadius: '32px',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          margin: '0 4px',
          minWidth: 'auto',

          // Typography for navigation buttons
          fontFamily: designTokens.typography.fontFamily.primary,
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.body2.fontWeight,
          lineHeight: theme.typography.body2.lineHeight,

          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out',
          },

          '&:disabled': {
            backgroundColor: theme.palette.action.disabled,
            color: theme.palette.action.disabledBackground,
          },

          // Icons within navigation buttons
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
            color: 'inherit',
          },
        },

        // Page number display
        '& .MuiTablePagination-displayedRows': {
          fontFamily: designTokens.typography.fontFamily.primary,
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.body2.fontWeight,
          color: theme.palette.text.primary,
          margin: '0 16px',
        },

        // Rows per page section
        '& .MuiTablePagination-selectLabel': {
          fontFamily: designTokens.typography.fontFamily.primary,
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.body2.fontWeight,
          color: theme.palette.text.primary,
        },

        // Select dropdown for rows per page
        '& .MuiTablePagination-select': {
          fontFamily: designTokens.typography.fontFamily.primary,
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.body2.fontWeight,
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
          border: 'none',

          '&:focus': {
            backgroundColor: 'transparent',
          },
        },

        // Input label for rows per page
        '& .MuiTablePagination-input': {
          fontFamily: designTokens.typography.fontFamily.primary,
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.text.primary,
        },
      }),
    },
  },
  MuiPagination: {
    styleOverrides: {
      root: () => ({
        // Main pagination container
        '& .MuiPagination-ul': {
          gap: 0, // Remove gap since we use margin on individual items
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center', // Keep pagination buttons centered within component
        },
      }),
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Page number circles
        width: '32px',
        height: '32px',
        borderRadius: '32px',
        backgroundColor: `transparent`,
        border: 'none !important', // Remove borders for cleaner look
        color: theme.palette.text.primary,
        minWidth: '32px',
        margin: '0 2px', // Add small margin between buttons
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',

        // Typography for page numbers
        fontFamily: designTokens.typography.fontFamily.primary,
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
        lineHeight: theme.typography.body2.lineHeight,

        '&:hover': {
          backgroundColor: `${theme.palette.grey[200]} !important`,
          transform: 'scale(1.05)',
          transition: 'all 0.2s ease-in-out',
        },

        // Selected page number
        '&.Mui-selected': {
          backgroundColor: `${theme.palette.primary.main} !important`,
          color: `${theme.palette.primary.contrastText} !important`,
          border: 'none', // Remove border for selected state

          '&:hover': {
            backgroundColor: `${theme.palette.primary.dark} !important`,
            transform: 'scale(1.05)',
          },
        },

        // Disabled state
        '&.Mui-disabled': {
          backgroundColor: `${theme.palette.grey[50]} !important`,
          color: theme.palette.text.disabled,
          border: 'none !important',

          '&:hover': {
            transform: 'none', // No hover effect for disabled
          },
        },

        // Navigation arrows (< >)
        '&.MuiPaginationItem-previousNext': {
          backgroundColor: `${theme.palette.grey[100]} !important`, // Same as regular buttons
          color: theme.palette.text.primary,
          border: 'none !important',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          '&:hover': {
            backgroundColor: `${theme.palette.grey[200]} !important`,
            transform: 'scale(1.05)',
          },

          '&.Mui-disabled': {
            backgroundColor: `${theme.palette.grey[50]} !important`,
            color: theme.palette.text.disabled,
            border: 'none !important',

            '&:hover': {
              transform: 'none',
            },
          },
        },

        // Ellipsis (...)
        '&.MuiPaginationItem-ellipsis': {
          backgroundColor: 'transparent',
          border: 'none',
          color: theme.palette.text.secondary,

          '&:hover': {
            backgroundColor: 'transparent',
            transform: 'none', // No hover effect for ellipsis
          },
        },
      }),
    },
  },
};
